import FormControl from '@mui/material/FormControl';
import {
  Autocomplete,
  Box,
  Chip,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

type Props = {
  label?: string;
  placeholder?: string;
  value: string[];
  options: any[];
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  loadingText?: string;
  error?: string;
  sx?: any;
  getOptionLabel?: (option: any) => string;
  getOptionDescription?: (option: any) => string | undefined;
  onChange: (value: string[]) => void;
};

const MultiSelectFree = ({
  label,
  placeholder,
  value = [],
  options = [],
  required,
  disabled,
  loading,
  loadingText,
  error,
  sx,
  getOptionLabel,
  getOptionDescription,
  onChange,
}: Props) => {
  return (
    <FormControl fullWidth sx={{ marginTop: 0 }}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <Autocomplete
        multiple
        value={value}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        options={options}
        loading={loading}
        loadingText={loadingText || 'Loading...'}
        autoHighlight
        filterSelectedOptions
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        getOptionLabel={(option) => {
          if (getOptionLabel) {
            return getOptionLabel(option);
          }
          if (typeof option === 'string') {
            return option;
          }
          return option?.name || option?.label || String(option);
        }}
        filterOptions={(optionsList, params) => {
          const query = params.inputValue.trim().toLowerCase();
          const filtered = optionsList.filter((option) => {
            if (!query) return true;
            const raw = String(option).toLowerCase();
            const optLabel = getOptionLabel ? getOptionLabel(option).toLowerCase() : raw;
            const desc = getOptionDescription ? (getOptionDescription(option) || '').toLowerCase() : '';
            return raw.includes(query) || optLabel.includes(query) || desc.includes(query);
          });

          const trimmed = params.inputValue.trim();
          if (trimmed !== '') {
            const isExisting = optionsList.some((option) => {
              const raw = String(option).toLowerCase();
              const optLabel = getOptionLabel ? getOptionLabel(option).toLowerCase() : raw;
              return raw === trimmed.toLowerCase() || optLabel === trimmed.toLowerCase();
            });
            if (!isExisting) {
              filtered.push(trimmed);
            }
          }

          return filtered;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const isExisting = options.includes(option);
          const displayLabel = getOptionLabel
            ? getOptionLabel(option)
            : typeof option === 'string'
              ? option
              : option?.name || option?.label || String(option);
          const displayDesc = getOptionDescription ? getOptionDescription(option) : undefined;

          return (
            <li
              key={key}
              {...optionProps}
              style={{
                padding: '8px 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {isExisting ? displayLabel : `Add "${displayLabel}"`}
                </Typography>
                {!isExisting && (
                  <Chip
                    label="Custom"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '9px', height: 18, ml: 1, fontWeight: 700 }}
                  />
                )}
              </Box>
              {displayDesc && isExisting && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', mt: 0.25 }}>
                  {displayDesc}
                </Typography>
              )}
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const displayLabel = getOptionLabel ? getOptionLabel(option) : String(option);
            return (
              <Chip
                key={key}
                label={displayLabel}
                size="small"
                {...tagProps}
                sx={{
                  borderRadius: '6px',
                  fontSize: '11px',
                  height: 22,
                  fontWeight: 600,
                  bgcolor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    color: '#64748b',
                    '&:hover': { color: '#0f172a' },
                  },
                }}
              />
            );
          })
        }
        noOptionsText="No matching options. Press Enter to add."
        sx={{ width: '100%', ...sx }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? (placeholder || 'Type to search or add...') : ''}
            variant="outlined"
            margin="dense"
            error={error !== undefined}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {value.length === 0 && (
                    <InputAdornment position="start" sx={{ color: 'text.disabled', ml: 0.5, mr: -0.5 }}>
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        fullWidth
        size="small"
        disabled={disabled}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default MultiSelectFree;
